import {
  Button,
  type ColumnsType,
  ConfirmModal,
  ExpandedRowKeysProvider,
  Loading,
  PageContent,
  PageHeader,
  Table,
  TableRowMenuCell,
  Text,
  ToggleAllButton,
  useExpandedRowKeysContext,
} from '@/components';
import { MasterTableResourceGuard } from '@/features/authentication/_components/MasterTableResourceGuard';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { fillZeroToPolicyForRS } from '@/features/master-table/utils';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_DOUBLE_ICONS_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  getRecursiveArray,
  getRecursiveIds,
  isRS,
} from '@/utils';
import { type FC, type PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import invariant from 'tiny-invariant';
import { CreatePolicyButton } from '../_components/CreatePolicyButton';
import {
  DrawerStateProvider,
  useDrawerState,
} from '../_components/DrawerStateProvider';
import { PolicyFormDrawer } from '../_components/PolicyFormDrawer';
import { useDeletePolicy, usePolicies } from '../_hooks/policy';
import { PolicyLevelLabelModifier } from '../_models/policy';

export default function PoliciesPage() {
  return (
    <Layout>
      <Content />
    </Layout>
  );
}

const Layout: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();

  return (
    <DrawerStateProvider>
      <ExpandedRowKeysProvider>
        <PageHeader
          title={t('policies-page')}
          breadcrumbs={[
            {
              title: t('master-table-management'),
              href: '/master-table',
            },
            { title: t('policies-page') },
          ]}
          buttons={
            <MasterTableResourceGuard onlyEdit>
              <CreatePolicyButton />
            </MasterTableResourceGuard>
          }
        />
        <PageContent>{children}</PageContent>
        <PolicyFormDrawer />
      </ExpandedRowKeysProvider>
    </DrawerStateProvider>
  );
};

const Content: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const policies = usePolicies();
  const { openMessage } = useMessage();
  const { setDrawerState } = useDrawerState();
  const { mutateAsync: deleteMutateAsync } = useDeletePolicy();
  const editMasterTablePermission = useMasterTablePermission(true);
  const {
    expandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
  } = useExpandedRowKeysContext();

  const [deleteId, setDeleteId] = useState<string | null>(null);

  if (policies.isPending || !policies.data) {
    return <Loading />;
  }

  const dataSource = getRecursiveArray(
    policies.data.filter((d) => !d.upperId),
    policies.data,
  );

  const openAllExpanded = () => {
    setExpandedRowKeys(getRecursiveIds(dataSource));
  };

  return (
    <>
      <Table
        dataSource={dataSource}
        rowKey="id"
        title={() => (
          <ToggleAllButton
            isExpandedAllWhenMounted
            openAll={openAllExpanded}
            closeAll={clearExpandedRowKeys}
          />
        )}
        columns={[
          {
            title: t('level'),
            key: 'level',
            dataIndex: 'level',
            width: 150,
            render: (value) => PolicyLevelLabelModifier[value],
          },
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: (_, record) =>
              isRS() ? fillZeroToPolicyForRS(record.code) : record.code,
          },
          {
            title: t('name'),
            key: 'name',
            dataIndex: 'name',
            width: 300,
            render: (value) => <Text text={value} weight="regular" />,
          },
          {
            title: t('organization-in-charge'),
            key: 'organizationName',
            dataIndex: 'organizationName',
            width: 250,
            // TODO: Have the organization's name added to the response.
            render: (value) => <Text text={value} weight="regular" />,
          },
          {
            title: t('start-fiscal-year'),
            key: 'startYear',
            dataIndex: 'startYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('end-fiscal-year'),
            key: 'endYear',
            dataIndex: 'endYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('created-datetime'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          {
            title: t('updated-datetime'),
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          ...(isRS() && editMasterTablePermission.data
            ? ([
                {
                  title: '',
                  key: 'actions',
                  width: TABLE_DOUBLE_ICONS_WIDTH,
                  align: 'center',
                  render: (_, record) => (
                    <TableRowMenuCell
                      onEdit={() => {
                        setDrawerState({
                          open: true,
                          type: 'policy',
                          isEditing: true,
                          id: record.id,
                        });
                      }}
                      items={[
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () => setDeleteId(record.id),
                        },
                      ]}
                    />
                  ),
                },
              ] satisfies ColumnsType<(typeof dataSource)[number]>)
            : ([
                {
                  title: '',
                  key: 'actions',
                  width: TABLE_DOUBLE_ICONS_WIDTH,
                  align: 'center' as const,
                  render: (_, record) => (
                    <Button
                      onClick={() => {
                        navigate(record.id);
                      }}
                    >
                      {t('details')}
                    </Button>
                  ),
                },
              ] satisfies ColumnsType<(typeof dataSource)[number]>)),
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          rowExpandable: (record) =>
            Boolean(record.children && record.children.length > 0),
        }}
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
        }}
      />
      <ConfirmModal
        open={deleteId !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(deleteId, t('unreachable-code'));
          try {
            await deleteMutateAsync(deleteId);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setDeleteId(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setDeleteId(null)}
        skipCheck
      />
    </>
  );
};
